<template>
  <div>
    <MainLayout
      @stage-selected="handleStageSelected"
      @sidebar-toggled="handleSidebarToggled"
    >
      <template #default="{ currentStage }">
        <div class="home-content">
          <!-- 学习中心卡片 -->
          <div class="learning-center">
            <h2 class="center-title">
              <i class="el-icon-star-on"></i>
              学习中心
            </h2>
            <p class="center-subtitle">开始您的词汇学习之旅</p>

            <!-- 当前阶段信息 -->
            <div class="current-stage-info" v-if="currentStage">
              <div class="stage-card">
                <div class="stage-header">
                  <div class="stage-icon">
                    <i :class="currentStage.icon"></i>
                  </div>
                  <div class="stage-details">
                    <h3>{{ currentStage.name }}</h3>
                    <p>{{ currentStage.description }}</p>
                  </div>
                </div>

                <div class="home-stage-progress">
                  <div class="progress-info">
                    <span>学习进度</span>
                    <span
                      >{{ currentStage.masteredWords }}/{{
                        currentStage.wordCount
                      }}</span
                    >
                  </div>
                  <div class="progress-stats">
                    <span class="percentage"
                      >{{ Math.round(currentStage.progress) }}%</span
                    >
                    <span class="remaining"
                      >还需学习
                      {{ currentStage.wordCount - currentStage.masteredWords }}
                      个单词</span
                    >
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- 今日学习统计 -->
          <div class="today-summary">
            <h3 class="summary-title">
              <i class="el-icon-calendar"></i>
              今日学习概览
            </h3>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="item-icon reviewed">
                  <i class="el-icon-document"></i>
                </div>
                <div class="item-data">
                  <div class="data-number">{{ todayStats.reviewedCount }}</div>
                  <div class="data-label">复习单词</div>
                </div>
              </div>

              <div class="summary-item">
                <div class="item-icon new">
                  <i class="el-icon-plus"></i>
                </div>
                <div class="item-data">
                  <div class="data-number">{{ todayStats.newWordsCount }}</div>
                  <div class="data-label">新学单词</div>
                </div>
              </div>

              <div class="summary-item">
                <div class="item-icon time">
                  <i class="el-icon-timer"></i>
                </div>
                <div class="item-data">
                  <div class="data-number">
                    {{ formatStudyTime(todayStats.studyTime) }}
                  </div>
                  <div class="data-label">学习时长</div>
                </div>
              </div>

              <div class="summary-item">
                <div class="item-icon coins">
                  <i class="el-icon-coin"></i>
                </div>
                <div class="item-data">
                  <div class="data-number">{{ todayStats.earnedCoins }}</div>
                  <div class="data-label">获得金币</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 每日新单词 -->
          <div class="daily-new-words" v-if="dailyNewWords.length > 0">
            <h3 class="new-words-title">
              <i class="el-icon-star-on"></i>
              {{ isAlreadyAllocated ? '今日分配单词' : '今日新单词' }}
            </h3>
            <div class="allocation-info" v-if="allocationInfo">
              <p class="info-text">
                来自 <strong>{{ allocationInfo.stage_name }}</strong> - 
                <strong>{{ allocationInfo.unit_name }}</strong> 
                ({{ dailyNewWords.length }}个单词，{{ allocationInfo.allocation_date || '今日' }}分配)
              </p>
            </div>
            <div class="words-preview">
              <div 
                v-for="word in dailyNewWords.slice(0, 3)" 
                :key="word.id"
                class="word-preview-item"
              >
                <span class="word-english">{{ word.english_word }}</span>
                <span class="word-meaning">{{ word.chinese_meaning }}</span>
              </div>
              <div v-if="dailyNewWords.length > 3" class="more-words">
                还有 {{ dailyNewWords.length - 3 }} 个单词...
              </div>
            </div>
            <div class="new-words-actions">
              <el-button 
                v-if="!allocationInfo?.is_completed"
                type="primary" 
                @click="startLearningNewWords"
              >
                <i class="el-icon-edit"></i>
                {{ isAlreadyAllocated ? '继续学习单词' : '开始学习新单词' }}
              </el-button>
              <div v-else class="completed-status">
                <i class="el-icon-circle-check"></i>
                <span>今日单词已学完</span>
              </div>
            </div>
          </div>

          <!-- 学习建议 -->
          <div class="study-tips" v-if="studyTips.length > 0">
            <h3 class="tips-title">
              <i class="el-icon-light-bulb"></i>
              学习建议
            </h3>
            <div class="tips-list">
              <div
                v-for="(tip, index) in studyTips"
                :key="index"
                class="tip-item"
                :class="tip.type"
              >
                <i :class="tip.icon"></i>
                <span>{{ tip.message }}</span>
              </div>
            </div>
          </div>




        </div>
      </template>
    </MainLayout>


    <!-- 学习弹框 -->
    <WordReviewDialog
      v-model="showReviewDialog"
      :mode="dialogMode"
      :selected-stage="currentStageForDialog"
      @session-complete="handleSessionComplete"
      @closed="handleDialogClosed"
    />

    <!-- 强制复习弹窗 -->
    <ForcedReviewDialog
      v-model="showForcedReviewDialog"
      :review-words="reviewWords"
      @review-completed="handleReviewCompleted"
    />

  </div>
</template>

<script>
/* eslint-disable no-unused-vars */
import MainLayout from "@/components/MainLayout.vue";
import WordReviewDialog from "@/components/WordReviewDialog.vue";
import ForcedReviewDialog from "@/components/ForcedReviewDialog.vue";
import { homeApi } from "@/api/home";
import { UserSettingsAPI } from "@/api/userSettings";
import { mapState } from "vuex";

export default {
  name: "Home",
  components: {
    MainLayout,
    WordReviewDialog,
    ForcedReviewDialog,
  },
  data() {
    return {
      // 弹框控制
      showReviewDialog: false,
      dialogMode: "review", // 'review' 或 'learn'
      currentStageForDialog: null,

      // 强制复习弹窗
      showForcedReviewDialog: false,
      reviewWords: [],

      // 今日学习统计
      todayStats: {
        reviewedCount: 0,
        newWordsCount: 0,
        studyTime: 0, // 分钟
        earnedCoins: 0,
        accuracy: 85,
      },


      // 学习建议
      studyTips: [],

      // 新单词数量
      newWordCount: 100,

      // 每日新单词
      dailyNewWords: [],
      allocationInfo: null,
      isAlreadyAllocated: false, // 是否已经分配过
    };
  },

  computed: {
    ...mapState("user", ["userInfo"]),
    userId() {
      return this.userInfo?.id || 1; // 默认用户ID为1
    },
  },

  methods: {
    // 阶段选择处理
    // isUserAction: true=用户主动点击侧边栏切换；false=MainLayout 初始化恢复
    async handleStageSelected(stage, isUserAction = false) {
      // console.log("选择阶段：", stage, "主动:", isUserAction);
      this.loadStageData(stage);

      if (isUserAction) {
        // 用户主动切换：通知后端保存 current_stage_id + 清除今日未学习的旧分配 + 用新阶段重新分配
        try {
          await homeApi.switchStage(this.userId, stage.id);
          // 切换成功后刷新每日新单词展示（getDailyNewWords 会自动读取新阶段）
          await this.loadDailyNewWords();
          const stageName = stage.name || stage.stageName || '';
          this.$message.success(stageName ? `已切换到「${stageName}」` : '已切换学习阶段');
        } catch (error) {
          console.error('切换阶段失败:', error);
          this.$message.error('切换阶段失败，请重试');
        }
      } else {
        // 初始化恢复（切换页面/刷新后）：只加载当前阶段的今日单词展示，不重新分配
        this.loadDailyNewWords();
      }
    },

    // 侧边栏切换处理
    handleSidebarToggled() {
      console.log("侧边栏状态已切换");
    },

    // 加载阶段数据
    loadStageData(stage) {
      this.newWordCount = stage ? stage.wordCount - stage.masteredWords : 100;
      this.generateStudyTips(stage);
    },

    // 生成学习建议
    generateStudyTips(stage) {
      this.studyTips = [];

      if (stage && stage.progress < 30) {
        this.studyTips.push({
          type: "primary",
          icon: "el-icon-info",
          message: "刚开始学习这个阶段，建议每天学习10-15个新单词",
        });
      }

      if (this.todayStats.accuracy < 70) {
        this.studyTips.push({
          type: "warning",
          icon: "el-icon-warning",
          message: "准确率偏低，建议加强复习已学单词",
        });
      } else if (this.todayStats.accuracy > 90) {
        this.studyTips.push({
          type: "success",
          icon: "el-icon-check",
          message: "学习状态很好！可以适当增加新单词的学习量",
        });
      }

      this.studyTips.push({
        type: "info",
        icon: "el-icon-time",
        message: "建议每天保持30分钟以上的学习时间",
      });
    },

    // 开始复习
    startReview(stage) {
      // console.log("开始复习阶段：", stage);
      this.currentStageForDialog = stage;
      this.dialogMode = "review";
      this.showReviewDialog = true;
    },

    // 开始学习新单词
    startLearning(stage) {
      // console.log("开始学习新单词，阶段：", stage);
      this.currentStageForDialog = stage;
      this.dialogMode = "learn";
      this.showReviewDialog = true;
    },


    // 格式化学习时间
    formatStudyTime(minutes) {
      if (minutes < 60) {
        return `${minutes}分钟`;
      } else {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}小时${mins}分钟`;
      }
    },

    // 加载今日统计
    async loadTodayStats() {
      try {
        const response = await homeApi.getTodayStats(this.userId);
        // console.log("Home页面统计数据API响应:", response);
        
        // 由于API拦截器的处理，response已经是data.data的内容
        const data = response;
        
        // 检查数据结构，优先使用stats字段
        const stats = data.stats || data;
        
        this.todayStats = {
          reviewedCount: stats.reviewed_count || 0,
          newWordsCount: stats.new_words_count || 0,
          studyTime: stats.study_time || 0,
          earnedCoins: stats.earned_coins || 0,
          accuracy: stats.accuracy || 0,
        };
        
        // console.log("Home页面成功获取统计数据:", this.todayStats);
      } catch (error) {
        console.error("加载今日统计失败：", error);
        // 如果API调用失败，使用固定值而不是随机数
        this.todayStats = {
          reviewedCount: 0,
          newWordsCount: 0,
          studyTime: 0,
          earnedCoins: 0,
          accuracy: 0,
        };
      }
    },


    // 处理学习会话完成
    async handleSessionComplete(sessionData) {
      // console.log("学习会话完成：", sessionData);

      // 更新本地统计数据
      if (sessionData.mode === "review") {
        this.todayStats.reviewedCount += sessionData.totalWords;
      } else {
        this.todayStats.newWordsCount += sessionData.totalWords;
      }

      // 更新学习时长（sessionData.studyTime已经是毫秒，转换为分钟）
      const studyTimeMinutes = Math.max(1, Math.round(sessionData.studyTime / 60000));
      this.todayStats.studyTime += studyTimeMinutes;

      // 直接使用会话数据中的统计信息
      const correctWords = sessionData.correctWords || Math.round(sessionData.totalWords * (sessionData.accuracy / 100));
      const wrongWords = sessionData.totalWords - correctWords;
      
      // 更新准确率（使用会话的准确率）
      this.todayStats.accuracy = sessionData.accuracy || 0;

      // 更新到后端
      try {
        const updateData = {
          reviewed_count: this.todayStats.reviewedCount,
          new_words_count: this.todayStats.newWordsCount,
          study_time: studyTimeMinutes, // 使用本次学习的时长（分钟）
          earned_coins: sessionData.earnedCoins || this.todayStats.earnedCoins,
          total_words: sessionData.totalWords,
          correct_words: correctWords,
          wrong_words: wrongWords,
          review_sessions: sessionData.mode === "review" ? 1 : 0,
          learn_sessions: sessionData.mode === "learn" ? 1 : 0,
        };

        // console.log("发送统计数据到后端:", updateData);
        await homeApi.updateStats(this.userId, updateData);
      } catch (error) {
        console.error("更新统计数据失败：", error);
      }

      // 显示完成消息
      this.$message.success(
        `${sessionData.mode === "review" ? "复习" : "学习"}完成！`
      );

      // 如果是学习模式，重新加载今日分配单词状态
      if (sessionData.mode === "learn") {
        await this.loadDailyNewWords();
      }

      // 重新生成学习建议
      this.generateStudyTips(this.currentStageForDialog);
    },

    // 处理弹框关闭
    handleDialogClosed() {
      this.currentStageForDialog = null;
      this.dialogMode = "review";
    },

    // 检查复习单词
    async checkReviewWords() {
      try {
        // 1. 首先获取用户设置
        const settingsResponse = await UserSettingsAPI.getUserSettings();
        // console.log("用户设置API响应:", settingsResponse);
        
        let userSettings = {};
        if (settingsResponse.success && settingsResponse.data) {
          // 将设置数组转换为对象，便于查找
          if (Array.isArray(settingsResponse.data)) {
            settingsResponse.data.forEach(setting => {
              userSettings[setting.key] = setting.value;
            });
          } else if (typeof settingsResponse.data === 'object') {
            // 如果是按分类返回的对象，需要扁平化
            Object.values(settingsResponse.data).forEach(categorySettings => {
              if (Array.isArray(categorySettings)) {
                categorySettings.forEach(setting => {
                  userSettings[setting.key] = setting.value;
                });
              }
            });
          }
        }
        
        // console.log("解析后的用户设置:", userSettings);
        
        // 2. 检查是否禁用自动复习
        const cancelAutoReview = userSettings.cancel_auto_review;
        if (cancelAutoReview === 1 || cancelAutoReview === '1' || cancelAutoReview === true) {
          // console.log("用户已禁用自动复习，不显示强制复习弹窗");
          return;
        }
        
        // 3. 获取复习单词
        const response = await homeApi.getReviewWords();
        // console.log("复习单词API响应:", response);
        
        let reviewWords = [];
        if (Array.isArray(response)) {
          reviewWords = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          reviewWords = response.data;
        }
        
        if (reviewWords && reviewWords.length > 0) {
          // 4. 根据用户设置限制复习单词数量
          const maxReviewWords = userSettings.max_review_words;
          if (maxReviewWords && maxReviewWords > 0) {
            reviewWords = reviewWords.slice(0, maxReviewWords);
            // console.log(`根据用户设置限制复习单词数量: ${reviewWords.length} (最大: ${maxReviewWords})`);
          }
          
          this.reviewWords = reviewWords;
          this.showForcedReviewDialog = true;
          // console.log(`发现 ${reviewWords.length} 个需要复习的单词，显示强制复习弹窗`);

          // 预加载复习单词图片
          if (reviewWords.length > 0) {
            // console.log("🖼️ 开始预加载复习单词图片...");
            this.preloadImages(reviewWords).then(results => {
              const successful = results.filter(r => r.success).length;
              const failed = results.length - successful;
              // console.log(`🖼️ 复习单词图片预加载完成: 成功 ${successful}, 失败 ${failed}`);
            });
          }
        } else {
          // console.log("没有需要复习的单词");
        }
      } catch (error) {
        console.error("检查复习单词失败：", error);
      }
    },

    // 处理复习完成
    handleReviewCompleted() {
      this.showForcedReviewDialog = false;
      this.reviewWords = [];
      this.$message.success("复习完成！");
      
      // 重新加载统计数据
      this.loadTodayStats();
    },

    // 预加载图片工具函数
    preloadImages(words) {
      if (!words || !Array.isArray(words)) return Promise.resolve([]);
      
      const imagePromises = words
        .filter(word => word.image_url)
        .map(word => {
          return new Promise((resolve) => {
            const img = new Image();
            const imageUrl = this.getImageUrl(word.image_url);
            // console.log(`开始预加载图片地址: ${imageUrl}`);

            // 预先写入解析后的完整URL，组件可直接使用
            word._resolved_image_url = imageUrl;
            
            img.onload = () => {
              // 标记已成功预加载
              word._image_preloaded = true;
              // console.log(`✅ 预加载成功: ${word.english_word} - ${imageUrl}`);
              resolve({ word: word.english_word, success: true, url: imageUrl });
            };
            
            img.onerror = () => {
              word._image_preloaded = false;
              // console.warn(`❌ 预加载失败: ${word.english_word} - ${imageUrl}`);
              resolve({ word: word.english_word, success: false, url: imageUrl });
            };
            
            // 设置超时，避免长时间等待
            setTimeout(() => {
              if (!img.complete) {
                word._image_preloaded = false;
                // console.warn(`⏱️ 预加载超时: ${word.english_word} - ${imageUrl}`);
                resolve({ word: word.english_word, success: false, url: imageUrl, timeout: true });
              }
            }, 5000);
            
            img.src = imageUrl;
          });
        });
      
      return Promise.all(imagePromises);
    },

    // 获取图片完整URL
    getImageUrl(imageUrl) {
      if (!imageUrl) return "";
      
      // 如果已经是完整URL，直接返回
      if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
        return imageUrl;
      }
      
      // 拼接API基础URL和图片路径
      const { config } = require("@/config/env");
      let baseUrl = config.API_BASE_URL.replace("/api", "");
      if(!baseUrl) baseUrl = 'https://www.techbvocabulary.com/techb'
      // console.log(`拼接图片URL: 基础URL=${baseUrl}, 图片路径=${imageUrl}`);
      return baseUrl +'/'+ imageUrl;
    },

    // 加载每日新单词
    async loadDailyNewWords() {
      try {
        const response = await homeApi.getDailyNewWords(this.userId);
        // console.log("每日新单词API响应:", response);
        
        // 响应拦截器已经处理了success判断，直接使用返回的数据
        if (response && response.allocated_words !== undefined) {
          // 确保只显示今天分配的单词
          const allocatedWords = response.allocated_words || [];
          
          // 验证单词数据的完整性
          const validWords = allocatedWords.filter(word => 
            word && word.id && word.english_word && word.chinese_meaning
          );
          
          this.dailyNewWords = validWords;
          this.allocationInfo = response.allocation_info || null;
          
          // 判断是否已经分配过（根据消息内容判断）
          this.isAlreadyAllocated = response.message === '今日已分配过单词';
          
          // console.log(`成功加载 ${validWords.length} 个今日分配单词`);
          
          // 预加载每日新单词的图片
          if (validWords.length > 0) {
            // console.log("🖼️ 开始预加载每日新单词图片...");
            this.preloadImages(validWords).then(results => {
              const successful = results.filter(r => r.success).length;
              const failed = results.length - successful;
              // console.log(`🖼️ 每日新单词图片预加载完成: 成功 ${successful}, 失败 ${failed}`);
            });
          }
          
          // 如果分配信息存在，验证数量一致性
          if (this.allocationInfo && validWords.length > 0) {
            // console.log(`分配信息: ${this.allocationInfo.stage_name} - ${this.allocationInfo.unit_name}`);
          }
        } else {
          console.warn("获取每日新单词失败: 响应数据格式不正确");
          this.dailyNewWords = [];
          this.allocationInfo = null;
          this.isAlreadyAllocated = false;
        }
      } catch (error) {
        console.error("获取每日新单词失败：", error);
        this.dailyNewWords = [];
        this.allocationInfo = null;
        this.isAlreadyAllocated = false;
        // 静默处理，不显示错误弹窗
      }
    },

    // 开始学习新单词
    startLearningNewWords() {
      if (this.dailyNewWords.length === 0) {
        this.$message.warning("没有新单词可以学习");
        return;
      }
      
      // console.log("开始学习今日分配的单词:", this.dailyNewWords);
      
      // 使用现有的学习对话框，传入新单词数据
      this.currentStageForDialog = {
        id: this.allocationInfo?.stage_id || 1,
        name: this.allocationInfo?.stage_name || "新单词学习",
        description: "今日新分配的单词学习",
        initialWords: this.dailyNewWords // 传入今日分配的单词
      };
      this.dialogMode = "learn";
      this.showReviewDialog = true;
    },



  },

  async mounted() {
    await this.loadTodayStats();
    await this.loadDailyNewWords(); // 加载每日新单词

    this.generateStudyTips();
    
    // 检查复习单词
    this.checkReviewWords();
  },
};
</script>

<style scoped>
.home-content {
  max-width: 1000px;
  margin: 0 auto;
}

/* 学习中心 */
.learning-center {
  background: white;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.center-title {
  font-size: 28px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.center-title i {
  color: #f39c12;
}

.center-subtitle {
  color: #7f8c8d;
  font-size: 16px;
  margin-bottom: 32px;
}

/* 当前阶段信息 */
.current-stage-info {
  margin-bottom: 32px;
}

.stage-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 24px;
  color: white;
}

.stage-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.stage-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-right: 20px;
}

.stage-details h3 {
  font-size: 20px;
  margin-bottom: 8px;
}

.stage-details p {
  opacity: 0.9;
  font-size: 14px;
}

.home-stage-progress {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.progress-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  font-size: 14px;
}

.percentage {
  font-size: 18px;
  font-weight: 600;
}

.remaining {
  opacity: 0.8;
}


/* 今日学习统计 */
.today-summary {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.summary-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-title i {
  color: #3498db;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.item-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
}

.item-icon.reviewed {
  background: #3498db;
}

.item-icon.new {
  background: #27ae60;
}

.item-icon.time {
  background: #e67e22;
}

.item-icon.coins {
  background: #f39c12;
}

.item-data {
  flex: 1;
}

.data-number {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1;
}

.data-label {
  font-size: 12px;
  color: #7f8c8d;
  margin-top: 4px;
}

/* 每日新单词 */
.daily-new-words {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
  color: white;
}

.new-words-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.new-words-title i {
  color: #ffd700;
}

.allocation-info {
  margin-bottom: 16px;
}

.info-text {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
}

.words-preview {
  margin-bottom: 20px;
}

.word-preview-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 14px;
}

.word-english {
  font-weight: 600;
  color: #fff;
}

.word-meaning {
  color: rgba(255, 255, 255, 0.8);
}

.more-words {
  text-align: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 8px;
}

.new-words-actions {
  text-align: center;
}

.new-words-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  backdrop-filter: blur(10px);
}

.new-words-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.completed-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  backdrop-filter: blur(10px);
}

.completed-status i {
  font-size: 16px;
  color: #67c23a;
}

/* 学习建议 */
.study-tips {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* 复习提醒 */
.review-reminder {
  background: linear-gradient(135deg, #ff6b6b, #ee5a52);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(255, 107, 107, 0.3);
  animation: slideIn 0.5s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.reminder-content {
  display: flex;
  align-items: center;
  gap: 20px;
  color: white;
}

.reminder-icon {
  font-size: 32px;
  opacity: 0.9;
}

.reminder-text {
  flex: 1;
}

.reminder-text h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
}

.reminder-text p {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
}

.reminder-actions {
  display: flex;
  gap: 12px;
}

.reminder-actions .el-button {
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  backdrop-filter: blur(10px);
}

.reminder-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.reminder-actions .el-button--primary {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.reminder-actions .el-button--primary:hover {
  background: rgba(255, 255, 255, 0.3);
}

.tips-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tips-title i {
  color: #f39c12;
}

.tips-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
}

.tip-item.primary {
  background: #ecf5ff;
  color: #409eff;
}

.tip-item.success {
  background: #f0f9ff;
  color: #67c23a;
}

.tip-item.warning {
  background: #fdf6ec;
  color: #e6a23c;
}

.tip-item.info {
  background: #f4f4f5;
  color: #909399;
}

.tip-item i {
  font-size: 16px;
  flex-shrink: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .learning-center {
    padding: 20px;
  }

  .center-title {
    font-size: 24px;
  }

  .function-nav {
    flex-wrap: wrap;
    gap: 8px;
  }

  .nav-item {
    flex: 1 1 calc(50% - 4px);
    padding: 12px 16px;
  }

  .nav-item:not(:last-child)::after {
    display: none;
  }

  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .stage-header {
    flex-direction: column;
    text-align: center;
  }

  .stage-icon {
    margin-right: 0;
    margin-bottom: 16px;
  }
}

@media (max-width: 480px) {
  .home-content {
    padding: 0 8px;
  }

  .learning-center,
  .today-summary,
  .study-tips {
    padding: 16px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

}

</style>
